/**
 * @description 对象属性类
 * @author: 菩萨蛮
 * @date 2020/2/14 9:49 下午
 * @version V2.0.1
 *
 * 公司的业务千篇一律，复杂的代码好几百行
 *
 * 这个结构仅仅是第一版，后续需要引入别的类，以及添加isEmpty
 * v1.0.1 新增选择栏功能
 * v1.0.2 默认值初始化添加支持boolean类型
 * v1.0.3 新增字段：默认字段defaultValue
 * v1.0.4 新增字段：字段对象style
 * v1.1.0 优化value值设置方法
 * v1.1.1 修复defaultValue深拷贝的问题，改为浅拷贝
 * V2.0.0 字段模式大改，增删改查型字段改为内置对象
 * V2.0.1 指定默认class类型为OrderItem
 */

/**
 * 建立一个增删改查的对象
 * @param item 对象或值
 * @param defaultValue 默认值
 * @returns {{new: *, modify: *, check: *, table: *}|*}
 */
const objInit = (item, defaultValue) => {
  if (item === undefined) {
    return {
      table: defaultValue,
      new: defaultValue,
      modify: defaultValue,
      check: defaultValue
    };
  }
  if (item instanceof Object) {
    const value = key => (item[key] !== undefined ? item[key] : defaultValue);
    return {
      table: value('table'),
      new: value('new'),
      modify: value('modify'),
      check: value('check')
    };
  }
  return {
    table: item,
    new: item,
    modify: item,
    check: item
  };
};

export default class OrderItem {
  /**
   * 构造函数
   * @param orderItemObj.param String 后端参数名
   * @param orderItemObj.title String 参数中文标题
   * @param orderItemObj.value String 参数值
   * @param orderItemObj.defaultValue String 默认参数值，不可读取
   * @param orderItemObj.type String 参数类型
   * @param orderItemObj.required Boolean/Object 是否必填
   * @param orderItemObj.disabled Boolean/Object 是否需要置灰
   * @param orderItemObj.visible Boolean/Object 是否需要显示
   * @param orderItemObj.search Object 是否可搜索
   * @param orderItemObj.selectList Array 搜索列表
   * @param orderItemObj.modify Boolean/Object 是否可修改
   * @param orderItemObj.adscription Number 字段类型 0：订单 1：产品
   * @param orderItemObj.paramType Number 参数类型 0：系统参数 1：用户自定义
   * @param orderItemObj.style Object 用于某些组件中中所需的字段，tableParam也放在这里
   * @param orderItemObj.other String 其它参数
   * @param orderItemObj.sort Number 排序
   * @param orderItemObj.boxSearch Boolean 是否可在搜索框内搜索 tsSearch
   * @param orderItemObj.moreSearch Boolean 是否可在展开搜索 tsSearch
   * @param orderItemObj.moreSearchType String 展开搜索类型 tsSearch
   */
  constructor(orderItemObj) {
    // 指定默认class名称
    this.name = 'OrderItem';
    this._param = orderItemObj.param;
    this._title = orderItemObj.title;
    this._defaultValue = orderItemObj.defaultValue;
    this._type = orderItemObj.type || 'input';
    this._required = objInit(orderItemObj.required, false);
    this._disabled = objInit(orderItemObj.disabled, false);
    this._visible = objInit(orderItemObj.visible, true);
    this._search = orderItemObj.search || false;
    this._selectList = orderItemObj.selectList || [];
    this._modify = objInit(orderItemObj.modify, false);
    this._adscription = orderItemObj.adscription;
    this._paramType = orderItemObj.paramType;
    this._style = orderItemObj.style || {};
    this._other = orderItemObj.other;
    this._sort = orderItemObj.sort;
    this._boxSearch = objInit(orderItemObj.boxSearch, false);
    this._moreSearch = objInit(orderItemObj.moreSearch, false);
    this._moreSearchType = objInit(orderItemObj.moreSearchType, false);
    this.setDefaultValue(orderItemObj);
  }

  /**
   * 构造中设置value的方法
   * @param orderItemObj
   */
  setDefaultValue(orderItemObj) {
    // type为其他时
    let defaultValue = '';
    if (orderItemObj.type === 'boolean') {
      defaultValue = false;
    } else if (orderItemObj.type === 'array') {
      defaultValue = [];
    } else if (orderItemObj.type === 'time') {
      defaultValue = null;
    } else if (orderItemObj.type === 'object') {
      defaultValue = {};
    }
    // 设定defaultValue初始值完毕
    if (orderItemObj.defaultValue !== undefined) {
      this._defaultValue = JSON.parse(JSON.stringify(orderItemObj.defaultValue));
      this._value = orderItemObj.value !== undefined ? orderItemObj.value : this._defaultValue;
    } else {
      this._value = orderItemObj.value !== undefined ? orderItemObj.value : defaultValue;
    }
  }

  get obj() {
    return {
      param: this._param,
      title: this._title,
      value: this._value,
      type: this._type,
      required: this._required,
      disabled: this._disabled,
      visible: this._visible,
      search: this._search,
      selectList: this._selectList,
      modify: this._modify,
      adscription: this._adscription,
      paramType: this._paramType,
      style: this._style,
      other: this._other,
      sort: this._sort,
      boxSearch: this._boxSearch,
      moreSearch: this._moreSearch,
      moreSearchType: this._moreSearchType
    };
  }


  get param() {
    return this._param;
  }

  get title() {
    return this._title;
  }

  get defaultValue() {
    return this._defaultValue;
  }

  get type() {
    return this._type;
  }

  get required() {
    return this._required;
  }

  get disabled() {
    return this._disabled;
  }

  get visible() {
    return this._visible;
  }

  get other() {
    return this._other;
  }

  get search() {
    return this._search;
  }

  get selectList() {
    return this._selectList;
  }

  set selectList(value) {
    this._selectList = value;
  }

  get modify() {
    return this._modify;
  }

  get adscription() {
    return this._adscription;
  }

  get paramType() {
    return this._paramType;
  }

  get style() {
    return this._style;
  }

  get sort() {
    return this._sort;
  }

  get value() {
    return this._value;
  }

  get boxSearch() {
    return this._boxSearch;
  }

  get moreSearch() {
    return this._moreSearch;
  }

  get moreSearchType() {
    return this._moreSearchType;
  }

  set value(value) {
    this._value = value;
  }

  set required(value) {
    this._required = value;
  }
}
